'use client';

import React, { useRef, useEffect, useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';

const featured = [
  { name: 'PENTAWIN-DSR', composition: 'Pantoprazole 40 mg + Domperidone 30 mg', category: 'Gastrointestinal Care', image: '/assets/images/5993de82-5b09-4844-bad8-8e869708a6ca-1789056112530.jpeg' },
  { name: 'EWINCET-O', composition: 'Cefixime 200 mg + Ofloxacin 200 mg', category: 'Anti-Infective', image: '/assets/images/34cf7b16-b104-446b-90ee-60f0fa162f7b-1789051227451.jpeg' },
  { name: 'EWIN-SP', composition: 'Aceclofenac 100 mg + Paracetamol 325 mg + Serratiopeptidase 15 mg', category: 'Pain & Inflammation', image: '/assets/images/2aea4275-b920-42a1-833c-9c210144e5da-1789051422389.jpeg' },
  { name: 'LIVOMONT', composition: 'Levocetirizine 5 mg + Montelukast 10 mg', category: 'Anti-Allergic', image: '/assets/images/98660145-df9d-4e9b-a1c5-f211a89eb0de-1789051227432.jpeg' },
  { name: 'EPOD-200', composition: 'Cefpodoxime Proxetil 200 mg', category: 'Anti-Infective', image: '/assets/images/72cd4d9d-e37e-41af-a1c6-6db4d19a6c09-1789051422355.jpeg' },
  { name: 'EWINCOF-PD', composition: 'Levosalbutamol + Ambroxol + Guaiphenesin', category: 'Respiratory', image: '/assets/images/c73fca46-0f1b-41fc-b9bb-8f27fdb2c14b-1789051227455.jpeg' },
];

export default function FeaturedProducts() {
  const trackRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.section-reveal').forEach((el) => el.classList.add('visible'));
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const checkScroll = () => {
    if (!trackRef.current) return;
    setCanScrollLeft(trackRef.current.scrollLeft > 10);
    setCanScrollRight(trackRef.current.scrollLeft < trackRef.current.scrollWidth - trackRef.current.clientWidth - 10);
  };

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    el.addEventListener('scroll', checkScroll, { passive: true });
    checkScroll();
    return () => el.removeEventListener('scroll', checkScroll);
  }, []);

  const scroll = (dir: 'left' | 'right') => {
    if (!trackRef.current) return;
    trackRef.current.scrollBy({ left: dir === 'left' ? -300 : 300, behavior: 'smooth' });
  };

  return (
    <section ref={sectionRef} className="py-20 bg-card border-y border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-10 gap-4 flex-wrap">
          <div className="section-reveal stagger-1 space-y-2">
            <span className="section-label">Featured</span>
            <h2 className="font-display text-foreground" style={{ fontSize: 'clamp(1.5rem, 2.5vw, 2rem)' }}>
              Featured Products
            </h2>
          </div>
          <div className="section-reveal stagger-2 flex items-center gap-3">
            <button
              onClick={() => scroll('left')}
              disabled={!canScrollLeft}
              className="carousel-arrow"
              aria-label="Scroll left"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                <path d="M11 4L6 9L11 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <button
              onClick={() => scroll('right')}
              disabled={!canScrollRight}
              className="carousel-arrow"
              aria-label="Scroll right"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                <path d="M7 4L12 9L7 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <Link href="/products" className="pharma-btn-primary ml-2 text-sm">
              View All
            </Link>
          </div>
        </div>

        <div ref={trackRef} className="featured-scroll-track section-reveal stagger-2">
          {featured.map((product) => (
            <div key={product.name} className="featured-scroll-item w-[260px] sm:w-[280px]">
              <div className="product-card h-full flex flex-col">
                {/* Image */}
                <div className="bg-gradient-to-b from-blue-50 to-green-50 flex items-center justify-center p-5 flex-shrink-0">
                  <AppImage
                    src={product.image}
                    alt={`${product.name} pharmaceutical product creative`}
                    width={260}
                    height={260}
                    className="object-contain w-full h-auto max-h-[220px]"
                    sizes="280px"
                  />
                </div>
                {/* Info */}
                <div className="p-5 flex flex-col flex-1 gap-2">
                  <span className="text-[10px] font-bold tracking-widest uppercase text-secondary">{product.category}</span>
                  <h3 className="font-display text-foreground text-base leading-tight">{product.name}</h3>
                  <p className="text-muted-foreground text-xs leading-relaxed flex-1">{product.composition}</p>
                  <Link
                    href="/products"
                    className="mt-2 text-primary text-xs font-bold hover:text-secondary transition-colors flex items-center gap-1"
                  >
                    View Product
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                      <path d="M3 7H11M8 4L11 7L8 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}